import { useMemo, useState } from 'react';

import { Box } from '@mui/material';

import PushScreen from '@/designs-dev/device/PushScreen';
import Navigation from '@/karehero-app/shell/Navigation';
import ShortlistHome from '@/karehero-app/care/shortlists/ShortlistHome';
import ShortlistView from '@/karehero-app/care/shortlists/ShortlistView';
import ProviderDetail from '@/karehero-app/care/shortlists/ProviderDetail';
import {
  SHORTLISTS,
  type ProviderStatus,
  type PublishedShortlist,
  type ShortlistProvider,
} from '@/karehero-app/care/shortlists/fixtures';

// Harness-only: drive the tab straight into one of its states without clicking
// through — open a shortlist, open a provider drawer, open the booking sheet, or
// force the empty / error home. The shared scene catalog
// (Shortlists.scenes.ts) maps each named state to one of these.
export type ShortlistsInitial = {
  shortlistId?: string;
  providerId?: string;
  booking?: boolean;
  empty?: boolean;
  error?: boolean;
};

/**
 * The Care tab's body: every shortlist the care expert has published, with the
 * shortlist and the provider pushed over it. No device chrome of its own — the
 * shell that mounts it owns the status bar and the tab bar — so the same tab
 * works inside the master app and inside Shortlists V1's trapped harness.
 *
 * It owns the family's secured-status overrides, because they are shared across
 * every shortlist a home appears in and both pushed pages can change them.
 */
const ShortlistsTab = ({
  shortlists = SHORTLISTS,
  error = false,
  initial,
  onNotifications,
  title,
  onBack,
}: {
  // Harness-only: override the published shortlists (e.g. [] for the empty
  // state) or force the load error, so the flows page can show those states
  // with the full tab bar + app bar chrome.
  shortlists?: PublishedShortlist[];
  error?: boolean;
  // Harness-only: seed which screen/state is open on mount.
  initial?: ShortlistsInitial;
  // Bell tap — opens the notification centre. Pass a no-op for an inert bell.
  onNotifications?: () => void;
  /**
   * The person being cared for, beside the logo. Optional so the master's own
   * bar is unchanged; the architecture cut passes it on every tab.
   */
  title?: string;
  /**
   * Pushed rather than a tab: a back where the root bar was. The care hub in
   * the architecture concept opens it from a section.
   */
  onBack?: () => void;
} = {}) => {
  const effectiveError = initial?.error ?? error;
  const effectiveShortlists = initial?.empty ? [] : shortlists;
  const [openShortlistId, setOpenShortlistId] = useState<string | null>(
    initial?.shortlistId ?? null,
  );
  const [openProviderId, setOpenProviderId] = useState<string | null>(
    initial?.providerId ?? null,
  );
  // Per-home secured status, keyed by provider id (shared across every
  // shortlist a home appears in). prevStatus remembers the status before
  // securing so un-securing can revert to it.
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, ProviderStatus>
  >({});
  const [prevStatus, setPrevStatus] = useState<Record<string, ProviderStatus>>(
    {},
  );

  // Apply the family's secured overrides on top of the CAM's published order.
  const resolvedShortlists = useMemo(
    () =>
      effectiveShortlists.map((sl) => ({
        ...sl,
        providers: sl.providers.map((p) => ({
          ...p,
          status: statusOverrides[p.id] ?? p.status,
        })),
      })),
    [effectiveShortlists, statusOverrides],
  );

  const openShortlist =
    resolvedShortlists.find((s) => s.id === openShortlistId) ?? null;
  const openProvider =
    resolvedShortlists
      .flatMap((s) => s.providers)
      .find((p) => p.id === openProviderId) ?? null;

  // Family toggles a home's secured status. provider is already resolved, so
  // provider.status is the current effective status. Un-securing reverts to the
  // remembered previous status, or Contacted if none was recorded.
  const toggleSecured = (provider: ShortlistProvider) => {
    const { id } = provider;
    if (provider.status === 'Secured') {
      const revert = prevStatus[id] ?? 'Contacted';
      setStatusOverrides((o) => ({ ...o, [id]: revert }));
    } else {
      setPrevStatus((p) => ({ ...p, [id]: provider.status }));
      setStatusOverrides((o) => ({ ...o, [id]: 'Secured' }));
    }
  };

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Root tab page: the shortlist home as a real page (no Back, app bar on
          top). */}
      <ShortlistHome
        appBar={
          onBack ? undefined : (
            <Navigation
              title={title}
              onNotifications={onNotifications}
              avatarInitials='OP'
            />
          )
        }
        onBack={onBack}
        shortlists={resolvedShortlists}
        error={effectiveError}
        onOpen={(s) => setOpenShortlistId(s.id)}
      />

      {/* The rest is a pushed stack over the tab page: shortlist → provider. */}
      {openShortlist && (
        <PushScreen onClose={() => setOpenShortlistId(null)}>
          {(close) => (
            <ShortlistView
              shortlist={openShortlist}
              onBack={close}
              onOpenProvider={(p) => setOpenProviderId(p.id)}
              onToggleSecured={toggleSecured}
            />
          )}
        </PushScreen>
      )}
      {openProvider && (
        <ProviderDetail
          asSheet
          provider={openProvider}
          initialBooking={Boolean(initial?.booking)}
          onBack={() => setOpenProviderId(null)}
          onToggleSecured={toggleSecured}
        />
      )}
    </Box>
  );
};

ShortlistsTab.displayName = 'ShortlistsTab';
export default ShortlistsTab;
